<script setup lang="tsx">
import HeaderComponent from '../shared/HeaderComponent.vue'
import { PermAdminJira, PermAdminCustomFields, PermAdminBackup } from '@/models/enums-generated'
import { hasPermission, viewing } from '@/services/utils'
import CustomFieldEditor from './CustomFieldEditor.vue'
import ConfigEdit from '../jira/ConfigEdit.vue'
import { computed, ref, watch } from 'vue'
import saveAs from 'file-saver'
import http from '@/services/http'
import logo from '@/assets/images/logo-full.svg'
import raulwalter from '@/assets/images/raulwalter.svg'

const hasJiraChanges = ref(false)
const hasCFChanges = ref(false)
const hasChanges = computed(() => hasCFChanges.value || hasJiraChanges.value)
const hasJiraPerm = computed(() => hasPermission(PermAdminJira))
const hasCFPerm = computed(() => hasCF.value && viewing.value > 0)
const hasCF = computed(() => hasPermission(PermAdminCustomFields))
const hasBackupPerm = computed(() => hasPermission(PermAdminBackup) && sessionMap.isPsql)
const hasAnyTab = computed(() => hasJiraPerm.value || hasCFPerm.value || hasBackupPerm.value)
const activeTab = ref<'jira' | 'custom_fields' | 'backup'>(
	hasJiraPerm.value ? 'jira' : hasCFPerm.value ? 'custom_fields' : hasBackupPerm.value ? 'backup' : 'jira',
)
const version = sessionMap.version

const isDownloading = ref(false)
async function downloadBackup() {
	isDownloading.value = true
	try {
		const { data } = await http.get<Blob>('/api/backup/download', { responseType: 'blob' })
		saveAs(data, `cybsis-backup-${new Date().toISOString().slice(0, 10)}.zip`)
	} finally {
		isDownloading.value = false
	}
}

watch([hasJiraPerm, hasCFPerm], ([hasJira, showCF]) => {
	if (!hasJira && showCF) activeTab.value = 'custom_fields'
})
</script>

<template>
	<HeaderComponent :title="$t('settings.label')"> </HeaderComponent>

	<ul v-if="hasAnyTab" class="nav nav-tabs" role="tablist">
		<li v-if="hasJiraPerm" class="nav-item" role="presentation">
			<button
				class="nav-link"
				:class="{ active: activeTab === 'jira', disabled: hasChanges }"
				id="jira-tab"
				type="button"
				role="tab"
				aria-controls="jira"
				:aria-selected="activeTab === 'jira'"
				@click="activeTab = 'jira'"
			>
				{{ $t('jira.label') }}
			</button>
		</li>
		<li v-if="hasCFPerm" class="nav-item" role="presentation">
			<button
				class="nav-link"
				:class="{ active: activeTab === 'custom_fields', disabled: hasChanges }"
				id="custom_fields-tab"
				type="button"
				role="tab"
				aria-controls="custom_fields"
				:aria-selected="activeTab === 'custom_fields'"
				@click="activeTab = 'custom_fields'"
			>
				{{ $t('customFields') }}
			</button>
		</li>
		<li v-if="hasBackupPerm" class="nav-item" role="presentation">
			<button
				class="nav-link"
				:class="{ active: activeTab === 'backup', disabled: hasChanges }"
				id="backup-tab"
				type="button"
				role="tab"
				aria-controls="backup"
				:aria-selected="activeTab === 'backup'"
				@click="activeTab = 'backup'"
			>
				{{ $t('settings.backup.tab') }}
			</button>
		</li>
	</ul>

	<div class="tab-content pt-3">
		<div v-if="!hasAnyTab && !hasCF">{{ $t('settings.adminRightsAreMissing') }}</div>
		<div v-else-if="!hasAnyTab">{{ $t('settings.customFieldsRights') }}</div>
		<div v-if="hasJiraPerm" class="tab-pane" :class="{ active: activeTab === 'jira' }" id="jira" role="tabpanel" aria-labelledby="jira-tab">
			<ConfigEdit @hasChanges="(val) => (hasJiraChanges = val)"></ConfigEdit>
		</div>

		<div
			v-if="hasCFPerm"
			class="tab-pane"
			:class="{ active: activeTab === 'custom_fields' }"
			id="custom_fields"
			role="tabpanel"
			aria-labelledby="custom_fields-tab"
		>
			<CustomFieldEditor @hasChanges="(val) => (hasCFChanges = val)"></CustomFieldEditor>
		</div>

		<div
			v-if="hasBackupPerm"
			class="tab-pane"
			:class="{ active: activeTab === 'backup' }"
			id="backup"
			role="tabpanel"
			aria-labelledby="backup-tab"
		>
			<p class="text-muted">{{ $t('settings.backup.description') }}</p>
			<button class="btn btn-primary" :disabled="isDownloading" @click="downloadBackup">
				<i class="bi bi-download me-1"></i>
				{{ $t('settings.backup.download') }}
			</button>
		</div>
	</div>

	<div class="version-info mt-3 ms-3" data-test="settings-version">
		<p class="mb-0 text-muted">Ver: {{ version }}</p>
	</div>
	<div class="version-info mt-3 ms-3">---</div>
	<div class="mt-1 ms-3 small-text" data-test="settings-E-ITS">
		<p class="mb-0 text-muted">{{ $t('settings.EITS') }}</p>
	</div>

	<a
		href="https://creativecommons.org/licenses/by-sa/4.0/deed.et"
		target="_blank"
		v-one-tooltip="{ content: 'https://creativecommons.org/licenses/by-sa/4.0/deed.et', placement: 'top-start' }"
	>
		<div class="license-box mt-1 ms-3 small-text" data-test="settings-CC">
			{{ $t('settings.CC') }}
		</div>
	</a>

	<div class="d-flex align-items-center mt-3 ms-3">
		<a href="https://eits.raulwalter.com">
			<img :src="logo" alt="Cybsis logo" height="32" />
		</a>

		<div class="ms-auto text-end">
			<a href="https://raulwalter.com">
				<img :src="raulwalter" alt="Raul Walter" height="32" />
			</a>

			<a href="mailto:eits@raulwalter.com" class="d-block text-primary text-decoration-none"> eits@raulwalter.com </a>
		</div>
	</div>
</template>

<style lang="sass" scoped>
@import '~bootstrap/scss/functions'
@import '~bootstrap/scss/variables'

.small-text
  font-size: $small-font-size

.disabled
  opacity: .6
  pointer-events: none
</style>
